import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { buildApiUrl } from "./apiConfig";
import { isCouponValid } from "./couponValidity";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Pagination,
  Checkbox,
  FormControlLabel,
  Typography,
  Chip,
  InputAdornment,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

export default function CouponsManager() {
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [couponsError, setCouponsError] = useState("");
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [onlyValidCoupons, setOnlyValidCoupons] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [couponOrders, setCouponOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [totalFilteredCoupons, setTotalFilteredCoupons] = useState(0);
  const [totalFilteredAmount, setTotalFilteredAmount] = useState(0);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const generateCouponCode = () => {
    return Math.random().toString(36).substring(2, 10); // ex: rvf7qwu3
  };
  const [newCoupon, setNewCoupon] = useState({
    code: "",
    amount: "",
    discount_type: "fixed_cart",
    description: "",
    date_expires: "",
    individual_use: false,
    exclude_sale_items: false,
    free_shipping: false,
    usage_limit: "",
    usage_limit_per_user: "",
    limit_usage_to_x_items: "",
    minimum_amount: "",
    maximum_amount: "",
    email_restrictions: [],
  });

  const ITEMS_PER_PAGE = 20;

  const fetchCoupons = useCallback(async () => {
    setLoadingCoupons(true);
    setCouponsError("");
    try {
      const res = await axios.get(
        buildApiUrl(
          `/api/coupons/fetchcoupons?page=${currentPage}&per_page=${ITEMS_PER_PAGE}`
        )
      );
      if (!Array.isArray(res.data)) {
        throw new Error("Răspuns invalid la încărcarea cupoanelor");
      }
      setCoupons(res.data);
      const total = parseInt(res.headers["x-total-pages"] || "1", 10);
      setTotalPages(total);
    } catch (err) {
      console.error("Eroare la fetch:", err);
      setCouponsError(
        "Cupoanele nu au putut fi încărcate din magazin. Încearcă din nou."
      );
    } finally {
      setLoadingCoupons(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = coupons.filter((coupon) => {
      const codeMatch = coupon.code?.toLowerCase().includes(term);
      const emailMatch = (coupon.email_restrictions || []).some((email) =>
        email.toLowerCase().includes(term)
      );
      const passesValidCheck = !onlyValidCoupons || isCouponValid(coupon);
      return (codeMatch || emailMatch) && passesValidCheck;
    });

    setFilteredCoupons(filtered);

    // ✅ actualizăm sumarul
    setTotalFilteredCoupons(filtered.length);
    const totalAmount = filtered.reduce(
      (sum, coupon) => sum + parseFloat(coupon.amount || 0),
      0
    );
    setTotalFilteredAmount(totalAmount);
  }, [coupons, searchTerm, onlyValidCoupons]);

  const handleEdit = async (coupon) => {
    setSelectedCoupon({ ...coupon });
    setOpenDialog(true);
    setCouponOrders([]); // reset
    setLoadingOrders(true); // începe încărcarea

    try {
      const res = await axios.get(
        buildApiUrl(
          `/api/coupons/orders/by-coupon/${encodeURIComponent(coupon.code)}`
        )
      );
      setCouponOrders(res.data);
    } catch (err) {
      console.error("Eroare la încărcarea comenzilor pentru cupon:", err);
    } finally {
      setLoadingOrders(false); // indiferent de rezultat vvv
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedCoupon((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    try {
      await axios.put(
        buildApiUrl(`/api/coupons/editcoupons/${selectedCoupon.id}`),
        selectedCoupon
      );
      setOpenDialog(false);
      fetchCoupons();
    } catch (err) {
      console.error("Eroare la salvare:", err);
    }
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Sigur vrei să ștergi acest cupon?");
    if (!confirm) return;

    try {
      await axios.delete(
        buildApiUrl(`/api/coupons/delete/${id}`)
      );

      setCoupons((prev) => prev.filter((c) => c.id !== id));
      setFilteredCoupons((prev) => prev.filter((c) => c.id !== id));

      setSnackbarMessage("Cuponul a fost șters cu succes!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Eroare la ștergere:", err);
      setSnackbarMessage("❌ Eroare la ștergerea cuponului.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      const { code } = selectedCoupon;
      const res = await axios.post(
        buildApiUrl(
          `/api/coupons/coupons/update?code=${encodeURIComponent(code)}`
        )
      );
      const updated = res.data?.updated?.[0];

      // ✅ Actualizăm instant valoarea în dialog
      setSelectedCoupon((prev) => ({
        ...prev,
        amount: updated.valoare_finala,
        usage_limit: (prev.usage_limit || 0) + 1,
        usage_limit_per_user: (prev.usage_limit_per_user || 0) + 1,
      }));

      setSnackbarMessage(
        `✅ Cuponul "${updated.code}" a fost actualizat. Valoare finală: ${updated.valoare_finala} RON`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      fetchCoupons();
    } catch (err) {
      console.error("Eroare la actualizarea cuponului:", err);
      setSnackbarMessage("❌ Eroare la actualizarea cuponului.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4">Cupoane & Vouchere</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setNewCoupon({
              code: generateCouponCode(),
              amount: "",
              discount_type: "fixed_cart",
              description: "",
            });
            setOpenCreateDialog(true);
          }}
        >
          Adaugă cupon
        </Button>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Caută după cod sau email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant={onlyValidCoupons ? "contained" : "outlined"}
          onClick={() => setOnlyValidCoupons((prev) => !prev)}
        >
          Afișează doar cupoanele valide
        </Button>
      </Stack>
      {/* ✅ SUMAR CUPON */}
      <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
        <Typography variant="subtitle1">
          <strong>{totalFilteredCoupons}</strong> cupoane afișate
        </Typography>
        <Typography variant="subtitle1">
          Valoare totală:{" "}
          <strong>
            {Math.round(totalFilteredAmount).toLocaleString("ro-RO")} RON
          </strong>
        </Typography>
      </Stack>

      {loadingCoupons && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <CircularProgress size={24} aria-label="Se încarcă cupoanele" />
          <Typography>Se încarcă cupoanele...</Typography>
        </Stack>
      )}
      {couponsError && (
        <MuiAlert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={fetchCoupons}>
              Reîncearcă
            </Button>
          }
        >
          {couponsError}
        </MuiAlert>
      )}
      {!loadingCoupons && !couponsError && filteredCoupons.length === 0 && (
        <MuiAlert severity="info" sx={{ mb: 3 }}>
          Nu există cupoane pentru filtrele selectate.
        </MuiAlert>
      )}

      <Grid container spacing={2}>
        {filteredCoupons.map((coupon) => (
          <Grid item xs={12} md={6} lg={4} key={coupon.id}>
            <div
              style={{
                border: "1px solid #ccc",
                padding: 16,
                borderRadius: 12,
              }}
            >
              <p>
                <strong>ID:</strong> {coupon.id}
              </p>
              <p>
                <strong>Cod:</strong> {coupon.code}
              </p>
              <p>
                <strong>Reducere:</strong> {coupon.amount}
              </p>
              <p>
                <strong>Client:</strong> {coupon.email_restrictions?.join(", ")}
              </p>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleEdit(coupon)}
                >
                  Editează
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDelete(coupon.id)}
                >
                  Șterge
                </Button>
              </Stack>
            </div>
          </Grid>
        ))}
      </Grid>

      <Grid container justifyContent="center" sx={{ mt: 4 }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editează Cupon</DialogTitle>
        <DialogContent>
          {selectedCoupon && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cod"
                  name="code"
                  value={selectedCoupon.code || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Valoare"
                  name="amount"
                  value={selectedCoupon.amount || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tip Discount"
                  name="discount_type"
                  value={selectedCoupon.discount_type || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descriere"
                  name="description"
                  value={selectedCoupon.description || ""}
                  onChange={handleChange}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Data expirării"
                  name="date_expires"
                  value={selectedCoupon.date_expires || ""}
                  onChange={handleChange}
                  type="datetime-local"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Limită totală utilizări"
                  name="usage_limit"
                  value={selectedCoupon.usage_limit || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Limită pe utilizator"
                  name="usage_limit_per_user"
                  value={selectedCoupon.usage_limit_per_user || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Limită pe X produse"
                  name="limit_usage_to_x_items"
                  value={selectedCoupon.limit_usage_to_x_items || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Suma minimă"
                  name="minimum_amount"
                  value={selectedCoupon.minimum_amount || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Suma maximă"
                  name="maximum_amount"
                  value={selectedCoupon.maximum_amount || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCoupon.individual_use || false}
                      onChange={handleChange}
                      name="individual_use"
                    />
                  }
                  label="Utilizare individuală"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCoupon.exclude_sale_items || false}
                      onChange={handleChange}
                      name="exclude_sale_items"
                    />
                  }
                  label="Exclude produse la reducere"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCoupon.free_shipping || false}
                      onChange={handleChange}
                      name="free_shipping"
                    />
                  }
                  label="Transport gratuit"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Emailuri restricționate (separate prin virgulă)"
                  name="email_restrictions"
                  value={(selectedCoupon.email_restrictions || []).join(", ")}
                  onChange={(e) =>
                    setSelectedCoupon((prev) => ({
                      ...prev,
                      email_restrictions: e.target.value
                        .split(",")
                        .map((s) => s.trim()),
                    }))
                  }
                />
              </Grid>
              {loadingOrders ? (
                <Grid item xs={12} sx={{ textAlign: "center", mt: 4, mb: 4 }}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Se încarcă comenzile...
                  </Typography>
                  <CircularProgress />
                </Grid>
              ) : (
                couponOrders.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                        Comenzi asociate acestui cupon:
                      </Typography>
                    </Grid>

                    {couponOrders.map((order) =>
                      order.line_items.map((item, idx) => (
                        <Grid item xs={12} key={`${order.id}-${idx}`}>
                          <div
                            style={{
                              display: "flex",
                              gap: 16,
                              alignItems: "flex-start",
                              border: "1px solid #ddd",
                              borderRadius: 8,
                              padding: 16,
                            }}
                          >
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                width={100}
                                height={100}
                                style={{ objectFit: "cover", borderRadius: 8 }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div
                                style={{ marginTop: 10 }}
                                dangerouslySetInnerHTML={{ __html: item.name }}
                              />
                              <div style={{ marginTop: 8 }}>
                                <Typography variant="body2">
                                  Preț produs:{" "}
                                  <strong>{item.subtotal || "N/A"} RON</strong>
                                </Typography>
                                <Typography variant="body2">
                                  Comanda #{order.id} —{" "}
                                  {new Date(order.date).toLocaleDateString(
                                    "ro-RO"
                                  )}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        </Grid>
                      ))
                    )}

                    <Grid item xs={12} sx={{ mt: 1 }}>
                      {couponOrders.map((order) => (
                        <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                          Total comandă #{order.id}:{" "}
                          {(Number(order.valoare_factura) || 0).toFixed(2)} RON
                        </Typography>
                      ))}
                    </Grid>
                  </>
                )
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Anulează</Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateCoupon}
          >
            Actualizează cupon
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adaugă cupon</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cod cupon"
                value={newCoupon.code}
                onChange={(e) =>
                  setNewCoupon((prev) => ({ ...prev, code: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valoare reducere"
                value={newCoupon.amount}
                onChange={(e) =>
                  setNewCoupon((prev) => ({ ...prev, amount: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tip reducere"
                value={newCoupon.discount_type}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    discount_type: e.target.value,
                  }))
                }
                select
                SelectProps={{ native: true }}
              >
                <option value="fixed_cart">Fixed Cart</option>
                <option value="percent">Percent</option>
                <option value="fixed_product">Fixed Product</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descriere"
                value={newCoupon.description}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data expirării"
                type="datetime-local"
                value={newCoupon.date_expires}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    date_expires: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCoupon.individual_use}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        individual_use: e.target.checked,
                      }))
                    }
                  />
                }
                label="Utilizare individuală"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCoupon.exclude_sale_items}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        exclude_sale_items: e.target.checked,
                      }))
                    }
                  />
                }
                label="Exclude produse la reducere"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCoupon.free_shipping}
                    onChange={(e) =>
                      setNewCoupon((prev) => ({
                        ...prev,
                        free_shipping: e.target.checked,
                      }))
                    }
                  />
                }
                label="Transport gratuit"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limită totală utilizări"
                value={newCoupon.usage_limit}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    usage_limit: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limită pe utilizator"
                value={newCoupon.usage_limit_per_user}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    usage_limit_per_user: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limită pe X produse"
                value={newCoupon.limit_usage_to_x_items}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    limit_usage_to_x_items: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Suma minimă"
                value={newCoupon.minimum_amount}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    minimum_amount: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Suma maximă"
                value={newCoupon.maximum_amount}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    maximum_amount: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emailuri restricționate (separate prin virgulă)"
                value={(newCoupon.email_restrictions || []).join(", ")}
                onChange={(e) =>
                  setNewCoupon((prev) => ({
                    ...prev,
                    email_restrictions: e.target.value
                      .split(",")
                      .map((email) => email.trim())
                      .filter((email) => email.length > 0),
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Anulează</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await axios.post(
                  buildApiUrl("/api/coupons/create"),
                  newCoupon
                );
                setSnackbarMessage("Cupon adăugat cu succes!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                setOpenCreateDialog(false);
                fetchCoupons();
              } catch (err) {
                console.error("Eroare la crearea cuponului:", err);
                setSnackbarMessage("❌ Eroare la crearea cuponului.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
              }
            }}
          >
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
